import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Plus, Send, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { Seo } from "../components/Seo";

const STATUS = {
  OPEN: ["Offen", "bg-blue-100 text-blue-700"],
  IN_PROGRESS: ["In Bearbeitung", "bg-amber-100 text-amber-700"],
  WAITING_CUSTOMER: ["Antwort benötigt", "bg-purple-100 text-purple-700"],
  RESOLVED: ["Erledigt", "bg-green-100 text-green-700"],
  UNFOUNDED: ["Geschlossen", "bg-gray-100 text-gray-600"],
};
const inputCls = "w-full rounded-xl border border-border bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50";

function timeAgo(iso) {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 3600) return `vor ${Math.max(1, Math.floor(s / 60))} Min.`;
  if (s < 86400) return `vor ${Math.floor(s / 3600)} Std.`;
  return new Date(iso).toLocaleDateString("de-DE");
}

function Detail({ id, onBack }) {
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/support/my-tickets/${id}`).then(({ data }) => setData(data)).catch((e) => toast.error(errText(e))), [id]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="h-60 bg-muted rounded-2xl animate-pulse" />;
  const t = data.ticket;
  const [sl, sc] = STATUS[t.status] || [t.status, "bg-secondary"];
  return (
    <div data-testid="myticket-detail">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="w-4 h-4" /> Alle Anfragen</button>
      <div className="bg-white border border-border rounded-2xl p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs font-bold bg-secondary px-2 py-1 rounded">{t.ticket_id}</span>
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${sc}`}>{sl}</span>
        </div>
        <h2 className="font-bold text-lg mt-2">{t.subject}</h2>
        <div className="mt-4 space-y-3">
          {data.messages.map((m) => (
            <div key={m.id} className={`rounded-2xl p-3.5 text-sm ${m.author_type === "STAFF" ? "bg-accent/5 border border-accent/20" : "bg-secondary/60 border border-border"}`}>
              <p className="text-xs font-bold mb-1">{m.author_type === "STAFF" ? "KLEDIO Support" : "Du"} <span className="text-muted-foreground font-normal">· {timeAgo(m.created_at)}</span></p>
              <p className="whitespace-pre-wrap">{m.body}</p>
            </div>
          ))}
        </div>
        {!["RESOLVED", "UNFOUNDED"].includes(t.status) || true ? (
          <div className="flex gap-2 mt-4">
            <input className={inputCls} placeholder="Antwort schreiben…" value={reply} onChange={(e) => setReply(e.target.value)} data-testid="myticket-reply-input" />
            <button className="bg-accent text-white text-sm font-bold px-5 rounded-xl disabled:opacity-50 inline-flex items-center gap-1.5" disabled={busy || reply.trim().length === 0} data-testid="myticket-reply-btn"
              onClick={async () => { setBusy(true); try { await api.post(`/support/my-tickets/${id}/reply`, { message: reply }); setReply(""); load(); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); } }}>
              <Send className="w-3.5 h-3.5" /> Senden
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function MeineAnfragen() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ category: "ALLGEMEIN", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/support/my-tickets").then(({ data }) => setData(data)).catch((e) => toast.error(errText(e))), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/support/tickets", form);
      toast.success(`Anfrage erstellt (${r.ticket_id})`);
      setShowForm(false);
      setForm({ category: "ALLGEMEIN", subject: "", message: "" });
      load();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <Seo title="Meine Anfragen – KLEDIO" />
      {selected ? <Detail id={selected} onBack={() => { setSelected(null); load(); }} /> : (
        <>
          <div className="flex items-center gap-3 mb-6">
            <span className="w-11 h-11 rounded-2xl bg-accent/10 text-accent flex items-center justify-center"><LifeBuoy className="w-5 h-5" /></span>
            <div className="flex-1">
              <h1 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight">Meine Anfragen</h1>
              <p className="text-sm text-muted-foreground">Dein direkter Draht zum KLEDIO-Support.</p>
            </div>
            <button className="bg-accent text-white text-sm font-bold px-4 py-2.5 rounded-full inline-flex items-center gap-1.5 hover:opacity-90" onClick={() => setShowForm(!showForm)} data-testid="myticket-new-btn">
              <Plus className="w-4 h-4" /> Neue Anfrage
            </button>
          </div>
          {showForm && (
            <div className="bg-white border border-border rounded-2xl p-5 mb-5 space-y-3" data-testid="myticket-form">
              <select className="text-sm border border-border rounded-xl px-3 py-2.5 bg-white w-full" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="myticket-category-select">
                {Object.entries(data?.categories || { ALLGEMEIN: "Allgemeine Anfrage" }).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <input className={inputCls} placeholder="Betreff" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="myticket-subject-input" />
              <textarea className={`${inputCls} min-h-[110px]`} placeholder="Beschreibe dein Anliegen…" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="myticket-message-input" />
              <button className="bg-accent text-white text-sm font-bold px-6 py-2.5 rounded-full disabled:opacity-50" disabled={busy || form.subject.length < 3 || form.message.length < 10} onClick={create} data-testid="myticket-create-btn">Anfrage senden</button>
            </div>
          )}
          <div className="space-y-2.5" data-testid="mytickets-list">
            {data === null && <div className="h-32 bg-muted rounded-2xl animate-pulse" />}
            {data?.tickets.length === 0 && (
              <div className="bg-white border border-border rounded-2xl p-8 text-center">
                <p className="font-bold">Noch keine Anfragen</p>
                <p className="text-sm text-muted-foreground mt-1">Wenn du Hilfe brauchst, erstelle eine neue Anfrage – wir melden uns schnellstmöglich.</p>
              </div>
            )}
            {data?.tickets.map((t) => {
              const [sl, sc] = STATUS[t.status] || [t.status, "bg-secondary"];
              return (
                <button key={t.id} onClick={() => setSelected(t.id)} className="w-full bg-white border border-border rounded-2xl p-4 text-left hover:border-accent/50 transition-colors flex items-center gap-3" data-testid={`myticket-row-${t.ticket_id}`}>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-bold truncate">{t.subject}</span>
                    <span className="block text-xs text-muted-foreground">{t.ticket_id} · {data.categories?.[t.category] || t.category} · {timeAgo(t.updated_at)}</span>
                  </span>
                  <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full shrink-0 ${sc}`}>{sl}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
